/**
 * Shared loading skeleton for SuspenseDataGrid / OmnisearchDataGridV2.
 *
 * Previously `DataGridSkeleton` (in SuspenseDataGrid.tsx) and `DefaultSkeleton`
 * (in OmnisearchDataGridV2.tsx) were nearly identical implementations with
 * slightly different defaults. Consolidated here. See REVIEW.md item #8.
 */

import { Box, useTheme } from "@mui/material";

export interface DataGridSkeletonProps {
  /** Number of skeleton data rows to render. Default 10. */
  rows?: number;
  /** Column flex distribution. Default mirrors a typical 6-column grid. */
  columnFlexes?: number[];
  /** Render the footer / pagination band. Default true. */
  showFooter?: boolean;
}

const DEFAULT_COLUMN_FLEXES = [0.25, 0.1, 0.15, 0.3, 0.15, 0.05];

export function DataGridSkeleton({
  rows = 10,
  columnFlexes = DEFAULT_COLUMN_FLEXES,
  showFooter = true,
}: DataGridSkeletonProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const headerBg = isDark ? "grey.900" : "grey.50";
  const cellBg = isDark ? "grey.800" : "grey.200";

  return (
    <Box
      sx={{
        width: "100%",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          px: 2,
          py: 1.5,
          bgcolor: headerBg,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {columnFlexes.map((flex, i) => (
          <Box
            key={i}
            sx={{
              flex,
              height: 20,
              bgcolor: cellBg,
              borderRadius: 0.5,
            }}
          />
        ))}
      </Box>

      {/* Data rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            display: "flex",
            gap: 2,
            px: 2,
            py: 1.5,
            borderBottom: rowIndex < rows - 1 ? 1 : 0,
            borderColor: "divider",
          }}
        >
          {columnFlexes.map((flex, colIndex) => (
            <Box
              key={colIndex}
              sx={{
                flex,
                height: 16,
                bgcolor: cellBg,
                borderRadius: 0.5,
                // Vary widths slightly so the skeleton doesn't look perfectly aligned
                width: `${60 + ((rowIndex * colIndex * 7) % 40)}%`,
              }}
            />
          ))}
        </Box>
      ))}

      {showFooter && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1.5,
            bgcolor: headerBg,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Box sx={{ width: 120, height: 16, bgcolor: cellBg, borderRadius: 0.5 }} />
          <Box sx={{ display: "flex", gap: 1 }}>
            {[28, 28, 28].map((size, i) => (
              <Box key={i} sx={{ width: size, height: size, bgcolor: cellBg, borderRadius: 0.5 }} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
